import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, TextField, Button, CircularProgress } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SendIcon from '@mui/icons-material/Send';
import { clearDraft, readDraft, writeDraft } from './chatDraft';


interface MessageInputProps {
    handleSend: (text: string) => Promise<void> | void; // Теперь передаем текст прямо в функцию
    takePhoto: () => Promise<void> | void;
    isMobile?: boolean;
    socket: any;
    id?: string;
    currentUser?: any;
}

// 1. Используем React.memo, чтобы ввод не зависел от рендеров списка сообщений
export const MessageInput: React.FC<MessageInputProps> = React.memo(({
                                                                         handleSend,
                                                                         takePhoto,
                                                                         isMobile,
                                                                         socket,
                                                                         id,
                                                                         currentUser
                                                                     }) => {
    // 2. Используем реф вместо стейта для мгновенного отклика букв.
    //    Стейт хранит только «есть ли текст» — он нужен для disabled кнопки.
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const lastTypingTime = useRef<number>(0);
    const [hasText, setHasText] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // 3. Черновик по чату: восстанавливаем текст при открытии беседы и при
    //    переключении между чатами (компонент не перемонтируется).
    useEffect(() => {
        const draft = readDraft(id);
        if (inputRef.current) inputRef.current.value = draft;
        setHasText(draft.trim().length > 0);
    }, [id]);

    const onSendClick = async () => {
        const text = inputRef.current?.value.trim();
        if (!text || isSending) return;

        // Сообщение сразу появляется в ленте оптимистично, поэтому поле и
        // черновик чистим здесь же: при сбое текст переотправит «Повторить».
        if (inputRef.current) inputRef.current.value = ''; // Очищаем мгновенно
        clearDraft(id);
        setHasText(false);
        setIsSending(true);
        try {
            await handleSend(text);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Box sx={{
            p: 1,
            bgcolor: 'white',
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'flex-end',
            // Плавное поднятие при появлении клавиатуры (на некоторых браузерах)
            transition: 'padding 0.2s ease-in-out'
        }}>
            <IconButton onClick={takePhoto} color="primary" sx={{ mb: 0.5, mr: 0.5 }}>
                <PhotoCameraIcon />
            </IconButton>

            <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={5}
                inputRef={inputRef} // Привязываем реф
                size="small"
                placeholder="Сообщение..."
                onChange={(e) => {
                    const value = e.target.value;
                    // Черновик переживает переключение чата и перезагрузку.
                    writeDraft(id, value);
                    setHasText(value.trim().length > 0);

                    // Тайпинг шлем не чаще чем раз в 2 секунды
                    const now = Date.now();
                    if (socket && id && now - lastTypingTime.current > 2000) {
                        socket.emit('typing', { conversationId: id, userId: currentUser?.id });
                        lastTypingTime.current = now;
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                        e.preventDefault();
                        onSendClick();
                    }
                }}
                sx={{
                    ml: 1, mb: 1,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '20px',
                        bgcolor: '#f1f3f4',
                        padding: '8px 12px',
                        fontSize: '16px' // Важно против зума!
                    }
                }}
            />
            <Button
                variant="contained"
                onClick={onSendClick}
                // Пустой ввод отправить нельзя, а на время POST виден спиннер.
                disabled={isSending || !hasText}
                sx={{ ml: 2, mb: 1, borderRadius: '25px', px: 4, minWidth: isMobile ? 56 : 140 }}
            >
                {isSending ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : isMobile ? (
                    <SendIcon sx={{
                        fontSize: '24px',
                        ml: '3px',
                        color: 'white'
                    }} />
                ) : (
                    'ОТПРАВИТЬ'
                )}
            </Button>
        </Box>
    );
});
